import { z } from 'zod';

const nullableText = z.preprocess(
  value => (value === '' || value == null ? null : String(value)),
  z.string().nullable(),
);

const nullableNumber = z.preprocess(value => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}, z.number().nullable());

const requiredNumber = (schema: z.ZodNumber = z.number()) =>
  z.preprocess(value => {
    if (value === '' || value == null) return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value;
  }, schema);

const positiveInteger = requiredNumber(z.number().int().positive());
const nonNegativeInteger = requiredNumber(z.number().int().nonnegative());
const nullablePositiveInteger = z.preprocess(value => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : value;
}, z.number().int().positive().nullable());

const nullableBoolean = z.preprocess(
  value => (value === '' || value == null ? null : value),
  z.boolean().nullable(),
);

export const OpenWorkMapWorkTypeSchema = z.enum([
  'FORWARD_DELIVERY',
  'TRY_AT_HOME_DELIVERY',
  'RETURN_PICKUP',
  'STORE_PICKUP_TRANSFER',
]);

export const OpenWorkMapDueStatusSchema = z.enum(['PAST_TARGET', 'DUE_WITHIN_24_HOURS', 'OPEN']);

export const OpenWorkMapWorkRowSchema = z
  .object({
    work_type: OpenWorkMapWorkTypeSchema,
    work_id: positiveInteger,
    lifecycle_status: z.string().min(1),
    facility_id: positiveInteger,
    facility_name: z.string().min(1),
    source_facility_id: nullableNumber,
    destination_facility_id: nullableNumber,
    ready_since: nullableText,
    due_at: nullableText,
    due_status: OpenWorkMapDueStatusSchema,
    latitude: nullableNumber,
    longitude: nullableNumber,
    mappable: z.boolean(),
    unmapped_reason: nullableText,
    pincode: nullableText,
    area: z.string().min(1),
    address_snapshot: z.string().min(1),
    customer_name: nullableText,
    customer_phone: nullableText,
    customer_phone_country: nullableText,
    road_distance_km: nullableNumber,
    distance_evidence: nullableText,
    item_count: nonNegativeInteger,
    payment_method: z.string().min(1),
    is_exchange: z.boolean(),
    is_express: z.boolean().optional().default(false),
    courier_partner: nullableText,
    expected_local_courier: z
      .enum(['SELF', 'SELF_EXPRESS'])
      .nullable()
      .optional()
      .default(null),
    fulfillment_mode: z.string().min(1).optional().default('UNKNOWN'),
    on_hold: z.boolean(),
    rider_assigned: z.boolean(),
    rider_id: nullableNumber,
    rider_name: nullableText,
    rider_phone: nullableText,
    reason: z.string().min(1),
    total_eligible_rows: nonNegativeInteger,
  })
  .strict();

export const OpenWorkMapRiderRowSchema = z
  .object({
    rider_id: positiveInteger,
    rider_name: nullableText,
    rider_phone: nullableText,
    shift_id: positiveInteger,
    facility_id: positiveInteger,
    facility_name: z.string().min(1),
    shift_started_at: nullableText,
    latitude: nullableNumber,
    longitude: nullableNumber,
    accuracy_meters: nullableNumber,
    last_location_at: nullableText,
    tracking_status: nullableText,
    foreground_service_running: nullableBoolean,
    active_package_id: nullableNumber,
    active_return_id: nullableNumber,
    mappable: z.boolean(),
    location_confidence: z.enum([
      'RECENT',
      'LOW_ACCURACY',
      'TRACKING_OFF',
      'STALE',
      'NO_LOCATION',
      'NO_LOCATION_TIME',
      'INVALID_LOCATION',
      'OUTSIDE_DELHI_NCR',
    ]),
    total_active_riders: nonNegativeInteger,
  })
  .strict();

export const OpenWorkMapFacilityRowSchema = z
  .object({
    facility_id: positiveInteger,
    name: z.string().min(1),
    facility_type: z.string().min(1),
    latitude: nullableNumber,
    longitude: nullableNumber,
    pincode: nullableText,
    city: nullableText,
    enabled: z.literal(true),
    recent_non_test_order_count: nonNegativeInteger,
    latest_non_test_order_on: nullableText,
    mappable: z.boolean(),
    facility_group: z.enum(['LYSKRAFT', 'JIT_EXPRESS']).optional().default('LYSKRAFT'),
    is_jit_b2b2c_express: z.boolean().optional().default(false),
    vendor_id: nullablePositiveInteger.optional().default(null),
    vendor_name: nullableText.optional().default(null),
    express_delivery_zone: nullableText.optional().default(null),
  })
  .strict()
  .superRefine((row, context) => {
    if (row.facility_group === 'LYSKRAFT') {
      if (row.is_jit_b2b2c_express) {
        context.addIssue({
          code: 'custom',
          path: ['is_jit_b2b2c_express'],
          message: 'A Lyskraft facility cannot be classified as JIT Express',
        });
      }
      if (row.recent_non_test_order_count < 1 || !row.latest_non_test_order_on) {
        context.addIssue({
          code: 'custom',
          path: ['recent_non_test_order_count'],
          message: 'A physical Hub facility must have recent non-test order activity',
        });
      }
      return;
    }

    if (
      !row.is_jit_b2b2c_express ||
      row.facility_type !== 'VENDOR' ||
      row.vendor_id == null ||
      !row.vendor_name ||
      !row.express_delivery_zone
    ) {
      context.addIssue({
        code: 'custom',
        path: ['facility_group'],
        message: 'A JIT Express facility requires vendor identity and an operating zone',
      });
    }
  });

export const OpenWorkMapWorkRowsSchema = z.array(OpenWorkMapWorkRowSchema);
export const OpenWorkMapRiderRowsSchema = z.array(OpenWorkMapRiderRowSchema);
export const OpenWorkMapFacilityRowsSchema = z.array(OpenWorkMapFacilityRowSchema);

export type OpenWorkMapWorkType = z.infer<typeof OpenWorkMapWorkTypeSchema>;
export type OpenWorkMapDueStatus = z.infer<typeof OpenWorkMapDueStatusSchema>;
export type OpenWorkMapWorkRow = z.infer<typeof OpenWorkMapWorkRowSchema>;
export type OpenWorkMapRiderRow = z.infer<typeof OpenWorkMapRiderRowSchema>;
export type OpenWorkMapFacilityRow = z.infer<typeof OpenWorkMapFacilityRowSchema>;

export type OpenWorkMapQueryScope = {
  facilityIds: Array<number>;
};
